import type { Router } from 'vue-router'
import { setHttpHooks, tokenStore } from './http'
import { useAuthStore } from '@/stores/auth'
import { clearSession } from '@/stores/session'
import { useToast } from '@/composables/useToast'
import { CODE } from '@/constants/errorCodes'
import { redirectQueryOf } from '@/router/redirect'

/**
 * 把 http 层的失败分发接到路由与提示上。
 *
 * ⚠️ 这一步之前**漏了** —— `setHttpHooks` 一直只是导出着没人调用，
 * 于是 40101（登录过期）、40301（必须改密）、40311/40312（portal 闸）
 * 全都没有全局兜底，业务错误也不弹提示。类型检查与构建都发现不了这种"没接线"，
 * 因为每一处单独看都是合法的。记在。
 *
 * 接口层是 R-10 / R-17 / R-18 双重防护的第二道：
 * 前端门禁漏判时，后端会拦，而这里负责把拦截结果**变成正确的界面反应**，
 * 而不是让客户看到一句红字然后停在一个进不去的页面上。
 */
export function installHttpHooks(router: Router) {
  const toast = useToast()
  /** 同一页的并发请求会一起撞 401（出金页就同时发银行账号与逐账户试算），踢出只做一次 */
  let kickingOut = false

  setHttpHooks({
    onError: (e) => toast.error(e.message),

    onUnauthorized: (e) => {
      // 401 / 40101 未登录或登录过期是常态、不必解释；40111 会话被吊销（含被顶下线）与 40313 登录已停用要先把原因说清楚（后端文案，不翻译）
      if (e.code === CODE.SESSION_REVOKED || e.code === CODE.LOGIN_DISABLED) toast.error(e.message)
      // 整套会话清理（含账户 store 与查询缓存）；只清 token 会把上一位客户的数据留给下一位
      clearSession()
      const current = router.currentRoute.value
      if (current.name === 'login' || kickingOut) return
      kickingOut = true
      // 回跳目标过 redirectQueryOf：剥掉 ?blocked= 这类一次性门禁标记，
      // 否则每次会话过期重登都会把上一次的拦截弹窗再演一遍（见 router/redirect.ts）
      void Promise.resolve(router.replace({ name: 'login', query: redirectQueryOf(current.fullPath) }))
        .catch(() => undefined)
        .finally(() => {
          kickingOut = false
        })

      /**
       * 兜底：到点还没落到登录页就整页跳。token 已经清了，留在业务页面上
       * 客户看到的是一个没有登录态、还在渲染业务空态的壳
       * （出金页那张「请先添加银行账号」就是这么来的）。
       *
       * ⚠️ **必须延时**，不能在 replace 的 then 里立刻判。
       * 冷启动时守卫自己也会返回一次带 `?redirect=` 的登录页重定向，和这里的 replace 撞在一起；
       * 立刻判的话守卫那次还没落地（login 的 chunk 还在下载），兜底就会开火，
       * 把守卫带好的 `?redirect=` 整页冲掉 —— 客户登录后回不到原来那一页（实测过，别改回去）。
       */
      window.setTimeout(() => {
        if (!tokenStore.get() && router.currentRoute.value.name !== 'login') {
          window.location.assign(router.resolve({ name: 'login' }).href)
        }
      }, 2000)
    },

    onMustChangePassword: async () => {
      /**
       * 40301：必须先改密（R-18）。后端拦截器与网关对 **must_change 或 密码过期** 都发同一个 40301，
       * 这条码本身分不出是哪一种 —— 而改密页要分：首登强制（不要「当前密码」栏、后端按 must_change 放行不传 oldPassword）
       * 与 90 天过期（必须验旧密码）文案与表单都不同。
       *
       * 以前这里直接 `mustChangePassword = true`，于是**会话中途密码过期**被当成首登：旧密码栏被藏掉、提交必 422，
       * 客户只能退出重登。现在改成先拉 `/profile/me`（它在 40301 闸下放行），
       * 用后端返回的 `mustChangePassword` / `passwordExpireInDays` 决定走哪一种；`fetchMe` 会同步这两个字段。
       *
       * 接口 4.0 已把 passwordExpireInDays 补回，前端多数时候在登录 / 拉资料时就知道该改密了；
       * 这条 40301 是**第二道防护**（R-18 双重防护的接口侧），覆盖"页面开着不动、密码在此期间过期"这类情况。
       * 后端口径：过期态只放行改密、登出、profile/me，其余业务接口（含资金接口、含网关层）一律 40301。
       */
      const auth = useAuthStore()
      try {
        await auth.fetchMe()
      } catch {
        // /profile/me 都拉不到（断网 / 会话没了）：下面的兜底仍把人送去改密页，会话问题由 onUnauthorized 另行处理
      }
      // 后端说要改密、/profile/me 却两个字段都没立起来（旧版本后端 / 字段缺失）：按「过期」兜底，照常要验旧密码
      if (!auth.passwordExpired) auth.markPasswordExpired()
      if (router.currentRoute.value.name !== 'change-password') {
        void router.replace({ name: 'change-password' })
      }
    },

    onGateDenied: (e, url) => {
      /**
       * 40311 / 40312：前端门禁漏判、或客户在两个标签页里状态不同步。
       * 回概览并带上标记，由概览页弹对应的拦截弹窗（不弹 toast —— 需求 §7 要的是弹窗）。
       *
       * 两件事是后来加的：
       * ① 把**被拦的请求 url** 打到控制台。踢回概览这个动作本身不带任何线索，dev 上排查「进某页立刻被弹回」
       *    只能靠它 —— 元凶多半不是当前页自己的请求，而是上一页 / 布局里没关开关的查询。
       * ② 顺手 `fetchMe()` 重拉一次门禁状态。后端说不行而前端说行，多半是前端手里的 onboardingProgress 过期了
       *    （另一个标签页推进了流程、或后台改了状态）；不刷的话概览会按旧状态再次弹出同一个任务弹窗，客户点进去再被踢，成死循环。
       *    如果刷完前端仍然认为可以进（profile 与 activation 侧不一致），那就是后端两个服务口径不一致，前端无解，要转 ASKS。
       */
      // eslint-disable-next-line no-console
      console.warn(`[gate] ${e.code} ${url ?? ''} → 回概览`, e.message)
      void useAuthStore().fetchMe().catch(() => undefined)
      if (router.currentRoute.value.path !== '/') {
        void router.replace({ path: '/', query: { blocked: '1' } })
      }
    },
  })

  // 未捕获的接口异常兜底：不至于静默失败
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason as { name?: string; code?: number } | undefined
    if (reason?.name === 'ApiError') {
      ev.preventDefault()
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[api] 未处理的接口失败', reason)
      }
    }
  })
}
